import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip the /auth/me probe on public login pages to avoid noisy 401s in the console.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isPublic = path === "/login" || path === "/support/login";
    if (isPublic) {
      setUser(false);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (_) {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password, validateRole) => {
    const { data } = await api.post("/auth/login", { email, password });
    // If a role validator is provided and rejects, do NOT commit user state.
    // Logout server-side so cookies are cleared too.
    if (typeof validateRole === "function" && !validateRole(data.user)) {
      try { await api.post("/auth/logout"); } catch (_) {}
      const err = new Error("ROLE_MISMATCH");
      err.code = "ROLE_MISMATCH";
      err.user = data.user;
      throw err;
    }
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
